import React from 'react';
import { Link } from 'react-router-dom';

const PersonalForecastInfoPage: React.FC = () => {
  return (
    <div className="car-calculator-info-page">
      <div className="hero-section forecast">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">Personal Finance Forecast</h1>
            <p className="hero-description">
              Plan your financial future with month-by-month projections across all your accounts.
              Track assets, liabilities, cash flows, and net worth over time with multi-currency support.
            </p>
            <div className="cta-buttons">
              <Link to="/forecast" className="btn btn-primary btn-large">Start Forecasting</Link>
              <Link to="/" className="btn btn-secondary btn-large">Back to Home</Link>
            </div>
          </div>
        </div>
      </div>

      <section className="features-section">
        <div className="container">
          <h2>Why Use Our Personal Forecast?</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h3>Month-by-Month Projections</h3>
              <p>See your complete financial picture projected forward 1, 5, or 10 years with detailed account-level breakdowns.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">💰</div>
              <h3>Balance Sheet + Cash Flows</h3>
              <p>Track what you own and owe alongside all income and expenses. Inspired by how CPAs model personal finances.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔄</div>
              <h3>Multi-Currency Support</h3>
              <p>Hold accounts in CAD, USD, GBP, or EUR with automatic FX conversion to your base currency.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📅</div>
              <h3>Smart Scheduling</h3>
              <p>Handle biweekly pay with 3-paycheck month detection, annual bonuses, quarterly payments, and one-time events.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📈</div>
              <h3>Interest & Growth Tracking</h3>
              <p>Model savings interest, investment growth, and debt costs separately to understand your passive income ratio.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">⚠️</div>
              <h3>Overdraft Warnings</h3>
              <p>Get alerts when projected cash flows would cause account overdrafts so you can plan ahead.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="pricing-section">
        <div className="container">
          <h2>Features by Tier</h2>
          <div className="pricing-grid">
            <div className="pricing-card free">
              <h3>Free Analysis</h3>
              <div className="price">$0</div>
              <ul>
                <li>Unlimited accounts and cash flows</li>
                <li>Month-by-month projections up to 10 years</li>
                <li>Multi-currency with FX rates</li>
                <li>Biweekly pay schedule detection</li>
                <li>Nominal and real dollar views</li>
                <li>Dashboard, monthly, and account detail views</li>
              </ul>
              <Link to="/forecast" className="btn btn-primary">Start Free Forecast</Link>
            </div>
            <div className="pricing-card account">
              <h3>Account Features</h3>
              <div className="price">Free</div>
              <ul>
                <li>Save your forecast scenarios</li>
                <li>Load previous forecasts</li>
                <li>Compare different financial plans</li>
              </ul>
              <Link to="/forecast" className="btn btn-secondary">Create Account</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="faq-section">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>How is this different from a budget app?</h3>
              <p>Budget apps track past spending. Our forecast projects your finances forward month-by-month, showing where you'll be financially in the future based on your current accounts and cash flows.</p>
            </div>
            <div className="faq-item">
              <h3>What account types can I track?</h3>
              <p>Any account: chequing, savings, retirement (RRSP, TFSA, 401k, IRA), credit cards, mortgages, lines of credit, and investment accounts. Categorize them as current, savings, or retirement.</p>
            </div>
            <div className="faq-item">
              <h3>How does biweekly pay work?</h3>
              <p>Enter your first pay date and we calculate which months get 2 vs 3 paychecks. Those extra paycheck months are highlighted so you can plan for the windfall.</p>
            </div>
            <div className="faq-item">
              <h3>What is "Real Dollar" mode?</h3>
              <p>Real dollar mode adjusts future values for inflation, showing you what your money will actually be worth in today's purchasing power. Nominal mode shows the raw dollar amounts.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-Tool Recommendations */}
      <section style={{padding: '3rem 2rem', background: '#f9fafb'}}>
        <div style={{maxWidth: '900px', margin: '0 auto'}}>
          <h2 style={{textAlign: 'center', marginBottom: '0.5rem'}}>Explore More Financial Tools</h2>
          <p style={{textAlign: 'center', color: '#6b7280', marginBottom: '2rem'}}>One account, all the tools you need</p>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem'}}>
            <Link to="/rrsp" style={{textDecoration: 'none', color: 'inherit'}}>
              <div style={{background: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb', transition: 'box-shadow 0.2s'}}>
                <h3 style={{color: '#dc2626', marginBottom: '0.5rem'}}>Retirement Planner</h3>
                <p style={{color: '#6b7280', fontSize: '0.9rem', margin: 0}}>Plan your retirement with RRSP, TFSA, CPP, and OAS projections</p>
              </div>
            </Link>
            <Link to="/tax-tools" style={{textDecoration: 'none', color: 'inherit'}}>
              <div style={{background: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb', transition: 'box-shadow 0.2s'}}>
                <h3 style={{color: '#dc2626', marginBottom: '0.5rem'}}>Tax Tools</h3>
                <p style={{color: '#6b7280', fontSize: '0.9rem', margin: 0}}>Estimate income taxes and calculate payroll deductions</p>
              </div>
            </Link>
            <Link to="/housing" style={{textDecoration: 'none', color: 'inherit'}}>
              <div style={{background: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb', transition: 'box-shadow 0.2s'}}>
                <h3 style={{color: '#dc2626', marginBottom: '0.5rem'}}>Housing Calculator</h3>
                <p style={{color: '#6b7280', fontSize: '0.9rem', margin: 0}}>Compare renting vs buying with 25-year cost analysis</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <div className="bottom-cta">
        <div className="container">
          <h2>Ready to forecast your financial future?</h2>
          <Link to="/forecast" className="btn btn-primary btn-large">Start Your Personal Forecast</Link>
        </div>
      </div>
    </div>
  );
};

export default PersonalForecastInfoPage;
